import { useEffect } from "react";
import { TextPageLayout } from "@/components/TextPageLayout";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags } from "@/lib/seo";
import { generateWebPageSchema, addSchemaToPage } from "@/lib/schema";

export default function TermsPage() {
  useEffect(() => {
    const title = "Terms - Munthly";
    const description = "Read Munthly's terms and conditions. Understand the terms of use for our free budgeting tool and how we protect your data.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/terms");
    const ogCleanup = setOpenGraphTags(title, description, "/terms");
    const twitterCleanup = setTwitterCardTags(title, description);
    
    // Add schema for terms page
    const webPageSchema = generateWebPageSchema(
      "/terms",
      title,
      description
    );
    
    const schemaCleanup = addSchemaToPage(webPageSchema);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      schemaCleanup();
    };
  }, []);

  return (
    <TextPageLayout
      title="Terms and Conditions"
      subtitle={`Last updated: ${new Date().toLocaleDateString()}`}
    >
      <div className="space-y-6 text-base leading-relaxed">
        <p className="text-sm text-muted-foreground">
          Please read these Terms and Conditions carefully before using Munthly. By accessing or using our service, you agree to be bound by these terms.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
        <p>
          By creating an account, accessing, or using Munthly (the "Service"), you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you must not use the Service.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">2. Description of Service</h2>
        <p>
          Munthly is a free budgeting tool that helps users organize and track their income, expenses, provisions, and savings. The Service is provided for informational and organizational purposes only. Munthly does not connect to bank accounts, process payments, or provide real-time financial data.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">3. No Financial Advice</h2>
        <p className="font-semibold text-foreground">
          IMPORTANT: Munthly does not provide financial, investment, tax, or legal advice. The Service and the Munthly Method are provided for informational and organizational purposes only.
        </p>
        <p>
          All financial decisions are your sole responsibility. You acknowledge that:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>You are solely responsible for your financial decisions and the consequences thereof</li>
          <li>You should consult with qualified financial, tax, or legal professionals before making any significant financial decisions</li>
          <li>Munthly is not a substitute for professional financial advice</li>
          <li>The Munthly Method is a budgeting approach, not financial advice</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">4. Service Provided "As Is"</h2>
        <p>
          The Service is provided on an "AS IS" and "AS AVAILABLE" basis, without warranties of any kind, either express or implied, including but not limited to:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>Warranties of merchantability</li>
          <li>Fitness for a particular purpose</li>
          <li>Non-infringement</li>
          <li>Accuracy, completeness, or reliability of any information provided</li>
          <li>Uninterrupted or error-free operation</li>
        </ul>
        <p className="mt-4">
          We do not warrant that the Service will meet your requirements, be available at all times, or be free from errors, bugs, or defects.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">5. Limitation of Liability</h2>
        <p className="font-semibold text-foreground">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, MUNTHLY, ITS DEVELOPERS, OWNERS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>Your use or inability to use the Service</li>
          <li>Any errors, bugs, defects, or malfunctions in the Service</li>
          <li>Any interruption or cessation of the Service</li>
          <li>Any loss or corruption of data</li>
          <li>Any financial decisions or consequences arising from your use of the Service</li>
          <li>Any unauthorized access to or use of our servers or data</li>
          <li>Any third-party conduct or content</li>
        </ul>
        <p className="mt-4">
          Our total liability to you for all claims arising from or related to the Service shall not exceed the amount you paid to us (which is currently zero, as the Service is free) or £10, whichever is greater.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">6. User Responsibilities and Indemnification</h2>
        <p>
          You are solely responsible for:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>The accuracy and completeness of all data you enter into the Service</li>
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>All activities that occur under your account</li>
          <li>Your financial decisions and the consequences thereof</li>
          <li>Backing up your data</li>
          <li>Ensuring your use of the Service complies with all applicable laws</li>
        </ul>
        <p className="mt-4">
          You agree to indemnify, defend, and hold harmless Munthly, its developers, owners, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including reasonable attorney's fees) arising from your use of the Service, violation of these Terms, or infringement of any rights of another.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">7. Data Accuracy and Reliability</h2>
        <p>
          While we strive to provide accurate calculations and functionality, we do not guarantee:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>The accuracy of any calculations, projections, or estimates</li>
          <li>That the Service will be free from errors, bugs, or defects</li>
          <li>That data will not be lost, corrupted, or inaccessible</li>
          <li>That the Service will be available at all times</li>
        </ul>
        <p className="mt-4">
          You acknowledge that you should verify all calculations independently and not rely solely on the Service for financial planning.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">8. Account Security</h2>
        <p>
          You are responsible for maintaining the security of your account. You agree to:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>Keep your password confidential and secure</li>
          <li>Notify us immediately of any unauthorized access</li>
          <li>Accept responsibility for all activities under your account</li>
        </ul>
        <p className="mt-4">
          We are not liable for any loss or damage arising from unauthorized access to your account due to your failure to maintain account security.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">9. Service Availability and Modifications</h2>
        <p>
          We reserve the right to:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>Modify, suspend, or discontinue the Service at any time, with or without notice</li>
          <li>Perform maintenance that may result in service interruptions</li>
          <li>Change features, functionality, or pricing (currently free) at any time</li>
        </ul>
        <p className="mt-4">
          We are not liable for any loss or inconvenience resulting from service interruptions or discontinuation.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">10. Intellectual Property</h2>
        <p>
          The Service, including its design, functionality, and content, is protected by copyright, trademark, and other intellectual property laws. You may not:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>Copy, modify, or create derivative works of the Service</li>
          <li>Reverse engineer, decompile, or disassemble the Service</li>
          <li>Use the Service for any commercial purpose without permission</li>
          <li>Remove any copyright or proprietary notices</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">11. Third-Party Services</h2>
        <p>
          The Service may reference or recommend third-party services (such as banking apps). We are not responsible for:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2 text-muted-foreground">
          <li>The availability, accuracy, or functionality of third-party services</li>
          <li>Any transactions or interactions with third-party services</li>
          <li>Any loss or damage arising from your use of third-party services</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">12. Termination</h2>
        <p>
          We may terminate or suspend your access to the Service immediately, without prior notice, for any breach of these Terms. You may terminate your account at any time by discontinuing use of the Service.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">13. Changes to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. Material changes will be communicated through the Service. Your continued use of the Service after changes constitutes acceptance of the modified Terms. If you do not agree to the modified Terms, you must discontinue use of the Service.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">14. Severability</h2>
        <p>
          If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">15. Entire Agreement</h2>
        <p>
          These Terms constitute the entire agreement between you and Munthly regarding the Service and supersede all prior agreements and understandings.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">16. Contact</h2>
        <p>
          If you have questions about these Terms, please contact us through the Service.
        </p>

        <div className="bg-[var(--surface-container)] rounded-2xl p-6 mt-8 border-2 border-foreground/20">
          <p className="text-sm font-semibold mb-2">By using Munthly, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.</p>
          <p className="text-xs text-muted-foreground mt-2">
            These Terms are designed to protect both you and us. If you have concerns about any provision, please consult with a legal professional before using the Service.
          </p>
        </div>
      </div>
    </TextPageLayout>
  );
}
